import { useMemo, useRef, useState } from "react";
import { AuthContext } from "@/providers";
import { Header } from "@repo/ui/components/Page/Header";
import { Footer } from "@repo/ui/components/Page/Footer";
import { Link, Outlet, useNavigate, useParams } from "react-router-dom";
import { type CollapsibleSection, Drawer, type LinkItem } from "../drawer";
import {
  CalendarCheck2Icon,
  CalendarClockIcon,
  FolderTreeIcon,
  GlobeIcon,
  ListTodoIcon,
  ListTreeIcon,
  NetworkIcon,
  NewspaperIcon,
  PuzzleIcon,
  Table2Icon,
  User2Icon,
  Users2Icon,
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import { config } from "@/config";
import { AppUrlPath } from "@/lib/AppUrlPath";
import { ControlBar } from "./ControlBar";
import { useUserStore } from "@/stores/userStore";
import logo from "@/assets/logoHorizontal.png";
import { type TeamDisabled, TeamDisabledContext } from "./disableTeam";
import { apiService } from "@/services/apiService";
import { useGetTenantByFriendly } from "@/hooks/queryHooks/tenantHooks";

export const BasePage = () => {
  const { tenantId } = useParams();
  const [state, send] = AuthContext.useActor();
  const { data: tenant } = useGetTenantByFriendly(tenantId ?? "");
  const { availableTenants, user } = useUserStore();
  const navigate = useNavigate();

  const linkItems = useMemo<(LinkItem | CollapsibleSection)[][]>(() => {
    if (tenant && tenant.friendlyId) {
      const appPath = new AppUrlPath(tenant.id);
      return [
        [
          {
            label: "Dashboard",
            icon: <Table2Icon className="h-4 w-4 mr-2" />,
            href: appPath.tenantHomePath,
            minRole: "view",
          },
          {
            label: "Profile",
            icon: <User2Icon className="h-4 w-4 mr-2" />,
            href: "/app/profile",
            minRole: "view",
          },
        ],
        [
          {
            label: "Meetings",
            icon: <CalendarClockIcon className="h-4 w-4 mr-2" />,
            href: appPath.meetingsPath,
            minRole: "view",
          },
          {
            label: "Agenda Items",
            icon: <ListTodoIcon className="h-4 w-4 mr-2" />,
            href: appPath.agendaItemsPath,
            minRole: "submit",
          },
          {
            label: "Documents",
            icon: <FolderTreeIcon className="h-4 w-4 mr-2" />,
            href: appPath.documentsPath,
            minRole: "submit",
          },
        ],
        [
          {
            title: "Organization",
            icon: <NetworkIcon className="h-4 w-4 mr-2" />,

            minRole: "admin",
            linkItems: [
              {
                label: "Users",
                icon: <Users2Icon className="h-4 w-4 mr-2" />,
                href: appPath.usersPath,
                minRole: "admin",
              },
              {
                label: "Teams",
                icon: <Users2Icon className="h-4 w-4 mr-2" />,
                href: appPath.teamsPath,
                minRole: "admin",
              },
              {
                label: "Letterheads",
                icon: <NewspaperIcon className="h-4 w-4 mr-2" />,
                href: appPath.letterheadsPath,
                minRole: "admin",
              },
              {
                label: "Agenda Sections",
                icon: <ListTreeIcon className="h-4 w-4 mr-2" />,
                href: appPath.agendaModulesPath,
                minRole: "admin",
              },
              {
                label: "Agenda Templates",
                icon: <CalendarCheck2Icon className="h-4 w-4 mr-2" />,
                href: appPath.agendaTemplatesPath,
                minRole: "admin",
              },
              {
                label: "Integrations",
                icon: <PuzzleIcon className="h-4 w-4 mr-2" />,
                href: appPath.integrationsPath,
                minRole: "admin",
              },
              {
                label: "Org Settings",
                icon: <GlobeIcon className="h-4 w-4 mr-2" />,
                href: appPath.settingsPath,
                minRole: "admin",
              },
            ],
          },
        ],
      ];
    } else {
      return [
        [
          {
            label: "Profile",
            icon: <User2Icon className="h-4 w-4 mr-2" />,
            href: "/app/profile",
          },
        ],
      ];
    }
  }, [tenant]);

  const [teamDisabled, setTeamDisabled] = useState(false);

  const teamDisabledContext: TeamDisabled = Object.assign(useRef({}).current, {
    teamDisabled,
    setTeamDisabled,
    consumer: null,
  });

  return (
    <div className="flex flex-col h-screen">
      <div className="flex-shrink-0">
        {/* Header */}
        <Header
          loggedIn={state.matches("authenticated")}
          onProfile={() => {
            navigate("/app/profile");
          }}
          onLogout={() => {
            send({ type: "SIGNOUT" });
          }}
          user={user}
          onSubmitContact={async (data) => {
            await apiService.v1.submitContactForm({
              subject: data.subject,
              message: data.message,
              browserInfo: data.browser
                ? {
                    userAgent: navigator.userAgent,
                    platform: navigator.platform,
                  }
                : null,
            });
            toast(
              "Thank you for contacting us! We will try to get back with you as soon as we can.",
              { type: "success" }
            );
          }}
          helpVideosUrl="https://civi.link/docs/help/videos"
          faqsUrl="https://civi.link/docs/help/faqs"
          position="sticky"
          logo={
            <>
              <Link to="/app">
                <img src={logo} className="h-12" />
              </Link>
              <div className="pl-2 flex flex-row align-middle">
                <p className="text-primary text-xl pr-2">|</p>
                <p className="text-tertiary text-xl">
                  Board Management Platform
                </p>
              </div>
            </>
          }
        />
      </div>

      <div className="flex flex-grow overflow-hidden">
        {state.matches("authenticated") && linkItems && (
          <Drawer linkItems={linkItems} />
        )}
        {/* Main Content and Drawer */}
        <main className="base-page flex-1 flex-grow flex flex-col overflow-x-auto overflow-y-auto mt-20 mb-12">
          {state.matches("authenticated") && availableTenants.length > 0 && (
            <ControlBar teamDisabled={teamDisabled} />
          )}
          <div className="p-4 flex flex-grow">
            {state.matches("loading") ? (
              <div className="text-center grow flex items-center justify-center">
                Loading...
              </div>
            ) : (
              <TeamDisabledContext.Provider value={teamDisabledContext}>
                <Outlet />
              </TeamDisabledContext.Provider>
            )}
          </div>
        </main>
      </div>

      <div className="flex-shrink-0">
        {/* Footer */}
        <Footer
          title="CiviLink LLC &nbsp; | &nbsp; All rights reserved"
          copyright
          variant={config.env !== "production" ? "destructive" : "default"}
          position="sticky"
          className="h-12"
          rightChildren={
            <div className="flex flex-row gap-4">
              {config.env === "development" && <p>Development</p>}
              {config.env === "staging" && <p>Staging</p>}
              <pre
                className="cursor-pointer"
                title="Click to copy"
                onClick={() =>
                  navigator.clipboard.writeText("v" + config.version)
                }
              >{`v${config.version}-${__COMMIT_HASH__}`}</pre>
            </div>
          }
        />
      </div>

      <ToastContainer
        position="bottom-right"
        className="pointer-events-auto"
        pauseOnFocusLoss={false}
        hideProgressBar={true}
        autoClose={3000}
        newestOnTop
        closeOnClick
        theme="light"
        stacked
      />
    </div>
  );
};
